import { useNavigate } from 'react-router-dom';
import type { Task } from '../lib/types';
import {
  useCancelTask,
  useDeleteTask,
  useHoldTask,
  useRetryTask,
  useUnholdTask,
} from '../api/tasks';

interface Props {
  task: Task;
  projectSlug: string;
}

/**
 * Renders Stop / Hold / Resume / Delete based on the task's current
 * status. Buttons are wired to the matching mutation hooks; each one
 * confirms before firing because these are blast-radius actions.
 */
export default function TaskActions({ task, projectSlug }: Props) {
  const cancel = useCancelTask(task.id, projectSlug);
  const hold = useHoldTask(task.id, projectSlug);
  const unhold = useUnholdTask(task.id, projectSlug);
  const del = useDeleteTask(task.id, projectSlug);
  const retry = useRetryTask(task.id, projectSlug);
  const navigate = useNavigate();

  const isRunning = task.status === 'running' || task.status === 'planning';
  const isQueued = task.status === 'queued';
  const isHeld = task.status === 'held';
  const isPending =
    task.status === 'pending_approval' || task.status === 'pending_questions';
  const isFailed = task.status === 'failed' && Boolean(task.pipeline_task_id);

  const onCancel = () => {
    const msg = isRunning
      ? 'Stop the running pipeline? Any in-progress agent calls will be terminated. Partial work may be left in the working tree.'
      : 'Cancel this task? It won\'t run.';
    if (!window.confirm(msg)) return;
    cancel.mutate();
  };

  const onHold = () => {
    if (!window.confirm('Hold this task? The worker will skip it until you release it.')) return;
    hold.mutate();
  };

  const onUnhold = () => {
    unhold.mutate();
  };

  const onDelete = () => {
    if (!window.confirm('Delete this task permanently? Comments and event history go with it. This cannot be undone.')) return;
    del.mutate(undefined, {
      onSuccess: () => navigate(`/p/${projectSlug}`),
    });
  };

  const anyPending =
    cancel.isPending || hold.isPending || unhold.isPending || del.isPending || retry.isPending;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {isRunning && (
        <Button onClick={onCancel} disabled={anyPending} variant="danger">
          ■ Stop
        </Button>
      )}
      {isQueued && (
        <>
          <Button onClick={onHold} disabled={anyPending} variant="muted">
            ⏸ Hold
          </Button>
          <Button onClick={onCancel} disabled={anyPending} variant="muted">
            ✗ Cancel
          </Button>
          <Button onClick={onDelete} disabled={anyPending} variant="danger">
            🗑 Delete
          </Button>
        </>
      )}
      {isHeld && (
        <>
          <Button onClick={onUnhold} disabled={anyPending} variant="primary">
            ▶ Release to queue
          </Button>
          <Button onClick={onDelete} disabled={anyPending} variant="danger">
            🗑 Delete
          </Button>
        </>
      )}
      {isPending && (
        <Button onClick={onCancel} disabled={anyPending} variant="muted">
          ✗ Cancel without approving
        </Button>
      )}
      {isFailed && (
        <Button
          onClick={() => {
            if (
              window.confirm(
                task.approved
                  ? `Retry this task? It will resume execution from the last completed step (retry #${(task.retry_count ?? 0) + 1}).`
                  : `Retry this task? The architect will re-plan from scratch (retry #${(task.retry_count ?? 0) + 1}).`,
              )
            ) {
              retry.mutate();
            }
          }}
          disabled={anyPending}
          variant="primary"
        >
          🔄 Retry
        </Button>
      )}
      {(cancel.error || hold.error || unhold.error || del.error) && (
        <span className="text-xs text-red-700">
          {((cancel.error || hold.error || unhold.error || del.error) as Error).message}
        </span>
      )}
    </div>
  );
}

function Button({
  onClick,
  children,
  disabled,
  variant,
}: {
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  variant: 'primary' | 'muted' | 'danger';
}) {
  const styles =
    variant === 'primary'
      ? 'bg-emerald-600 text-white hover:bg-emerald-700'
      : variant === 'danger'
        ? 'bg-red-50 text-red-700 border border-red-200 hover:bg-red-100'
        : 'bg-white text-slate-700 border border-slate-300 hover:bg-slate-50';
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1 text-xs rounded ${styles} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}
